import React, { useState, useEffect } from "react";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress } from "@material-ui/core";
import { PlainTextField, PasswordTextField } from "components";
import { Club } from "api/clubs";
import i18n from "../i18n";

// prop types
interface RegisterDialogProps {
    club: Club | null,
    error: string | null,
    isLoading: boolean,
    showDialog: boolean,
    onClose: () => void,
    onSignUp: (email: string, password: string, firstName: string, lastName: string, clubId?: string) => void,
}

// register dialog
const RegisterDialog = ({ club, error, isLoading, showDialog, onClose, onSignUp }: RegisterDialogProps) => {
    // ui state
    const [emailAddress, setEmailAddress] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [password, setPassword] = useState("");

    // did hide hook -> clear input fields
    useEffect(() => {
        if (!showDialog) {
            setEmailAddress("");
            setFirstName("");
            setLastName("");
            setPassword("");
        }
    }, [showDialog]);

    // relevant errors
    const relevantErrors = ["AuthError", "InvalidParameterException", "UsernameExistsException"];

    // render
    return (
        <Dialog
            open={showDialog}
            onClose={onClose}
        >
            <DialogTitle>{i18n.signUp}</DialogTitle>
            <DialogContent dividers>

                {/* first name */}
                <PlainTextField
                    autoFocus
                    disabled={isLoading}
                    error={error === "firstOrLastNameEmpty"}
                    label={i18n.firstName}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                />

                {/* last name */}
                <PlainTextField
                    disabled={isLoading}
                    error={error === "firstOrLastNameEmpty"}
                    helperText={(error === "firstOrLastNameEmpty") && i18n.errors[error!]}
                    label={i18n.lastName}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                />

                {/* email */}
                <PlainTextField
                    disabled={isLoading}
                    error={relevantErrors.includes(error!)}
                    helperText={relevantErrors.includes(error!) && i18n.errors[error!]}
                    label={i18n.emailAddress}
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                />

                {/* password */}
                <PasswordTextField
                    disabled={isLoading}
                    error={error === "passwordPolicy"}
                    helperText={(error === "passwordPolicy") && i18n.errors[error!]}
                    label={i18n.password}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />

            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button
                    color="primary"
                    disabled={isLoading}
                    onClick={() => onSignUp(emailAddress, password, firstName, lastName, club?.id)}
                >
                    {i18n.signUp}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RegisterDialog;
